let observers = [];
let initialized = false;
const options = {
    rootMargin: '0px',
    delay: 100,
    easing: 'ease-in-out',
    duration: 600,
    once: true,
    threshold: 0.1,
};
const createObserver = (once) => {
    let animationFrameId = null;
    const pendingAnimations = [];
    const processPendingAnimations = () => {
        pendingAnimations.forEach((element, index) => {
            element.style.setProperty('--stagger-delay', `${index * 30}ms`);
            element.classList.add('fade-up-animate');
        });
        pendingAnimations.length = 0;
        animationFrameId = null;
    };
    return new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            const element = entry.target;
            const attrOnce = element.getAttribute('data-fade-up-once');
            const shouldOnce = attrOnce !== null ? attrOnce !== 'false' : once;
            if (entry.isIntersecting) {
                // Queue animation instead of applying immediately
                if (!pendingAnimations.includes(element)) {
                    pendingAnimations.push(element);
                }
                // Batch animations using requestAnimationFrame
                if (animationFrameId === null) {
                    animationFrameId = requestAnimationFrame(processPendingAnimations);
                }
                // Unobserve if once is true
                if (shouldOnce && entry.target) {
                    observers.forEach(obs => obs.unobserve(entry.target));
                }
            }
            else if (!shouldOnce) {
                element.classList.remove('fade-up-animate');
            }
        });
    }, {
        root: null,
        rootMargin: options.rootMargin,
        threshold: options.threshold,
    });
};
const observeElements = (elements, observer) => {
    elements.forEach((element) => {
        element.classList.add('fade-up-init');
        observer.observe(element);
    });
};
const setupMutationObserver = (observer) => {
    const mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (node instanceof HTMLElement) {
                    // Check if the added node itself has fade-up
                    if (node.dataset && node.dataset.fadeUp) {
                        node.classList.add('fade-up-init');
                        observer.observe(node);
                    }
                    // Check for fade-up elements within the added node
                    const fadeUpElements = node.querySelectorAll('[data-fade-up]');
                    fadeUpElements.forEach((el) => {
                        el.classList.add('fade-up-init');
                        observer.observe(el);
                    });
                }
            });
        });
    });
    mutationObserver.observe(document.documentElement, {
        childList: true,
        subtree: true,
    });
};
const refresh = () => {
    if (!initialized)
        return;
    // Disconnect existing observers
    observers.forEach(obs => obs.disconnect());
    observers = [];
    // Recreate and re-observe
    const observer = createObserver(options.once);
    observers.push(observer);
    const elements = Array.from(document.querySelectorAll('[data-fade-up]'));
    observeElements(elements, observer);
};
const init = (settings) => {
    Object.assign(options, settings);
    const body = document.querySelector('body');
    body.setAttribute('data-fade-up-easing', options.easing);
    body.setAttribute('data-fade-up-duration', options.duration.toString());
    body.setAttribute('data-fade-up-delay', options.delay.toString());
    const startInit = () => {
        const observer = createObserver(options.once);
        observers.push(observer);
        const elements = Array.from(document.querySelectorAll('[data-fade-up]'));
        observeElements(elements, observer);
        // Watch for dynamically added elements
        setupMutationObserver(observer);
        initialized = true;
    };
    if (['complete', 'interactive'].includes(document.readyState)) {
        startInit();
    }
    else {
        window.addEventListener('DOMContentLoaded', startInit);
    }
    // Refresh on orientation change (positions may change)
    let resizeScheduled = false;
    window.addEventListener('resize', () => {
        if (!resizeScheduled) {
            resizeScheduled = true;
            requestAnimationFrame(() => {
                refresh();
                resizeScheduled = false;
            });
        }
    });
    window.addEventListener('orientationchange', () => {
        requestAnimationFrame(() => {
            requestAnimationFrame(refresh);
        });
    });
};
init();
export { init, refresh };
